import { useEffect, useState, useContext, useRef } from "react";
import styled, { withTheme } from "styled-components/macro";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";

// Component imports
import { API } from "../../Consts/Api";
import { GlobalContext } from "../../Context/GlobalContext";
import UltimathLogo from "../../Components/UltimathLogo/UltimathLogo";
import Stepper from "../MemoryGame/Blocks/Stepper";
import CustomButton from "../../Components/CustomButton/CustomButton";
import GameRules from "../MemoryGame/Blocks/GameRules";
import Alert from "../../Components/Alert/Alert";
import speedArithmeticsVideo from "./Static/mental-math-guide.mp4";

// styled-components
const Container = styled.section`
  background: ${(props) => props.theme.forestGreen};
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .triangle-left {
    position: absolute;
    top: 0;
    left: 0;
  }
  .divide-svg {
    position: absolute;
    left: 5%;
    top: 50%;
    transform: translateY(-50%);
  }
  .plus-svg-left {
    position: absolute;
    bottom: 0;
    left: 0;
  }
  .plus-svg-right {
    position: absolute;
    top: 0;
    right: 0;
  }
  .triangle-right {
    position: absolute;
    right: 5%;
    top: 50%;
    transform: translateY(-50%);
  }
  .divider-right {
    position: absolute;
    bottom: 0;
    right: 0;
  }

  @media (max-width: 768px) {
    svg {
      display: none;
    }
  }
`;

const InnerContainer = styled.section`
  min-height: 100vh;
  width: 80%;
  max-width: 1235px;
  background: ${(props) => props.theme.warmBeige};
  padding: 55px 110px 0;

  .text-center {
    text-align: center;
  }

  .button-container {
    width: 250px;
    margin: 40px auto;
  }

  .button-container button {
    margin-bottom: 8px;
  }

  @media (max-width: 768px) {
    padding: 40px 16px 0;
    width: 100%;

    .button-container {
      margin: 40px auto;
    }
  }
`;
const GameTitle = styled.div`
  margin-top: 80px;
  text-align: center;

  h1 {
    color: #020202;
    text-align: center;
    font-family: Inter;
    font-size: 29px;
    font-style: normal;
    font-weight: 600;
    line-height: 40px;
  }

  p {
    color: #000;
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    margin-top: 16px;
  }

  @media (max-width: 768px) {
    margin-top: 48px;

    h1 {
      font-size: 27px;
      font-style: normal;
      font-weight: 700;
    }
    p {
      color: rgba(0, 0, 0, 0.5);
      text-align: center;
      font-size: 16px;
      line-height: 18px;
      margin-top: 10px;
    }
  }
`;
const GameDuration = styled.div`
  color: #005840;
  font-family: Inter;
  margin-top: 32px;
  p {
    font-size: 40px;
    font-weight: 700;
    line-height: 40px;
    text-transform: uppercase;
    display: inline-block;
  }

  span {
    font-size: 14px;
    font-weight: 700;
    line-height: 40px;
    text-transform: uppercase;
  }
`;
const Level = styled.p`
  color: #757575;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  text-transform: uppercase;
  text-align: center;
`;
const CurrentProblemContainer = styled.div`
  font-family: Inter;
  margin-top: 3px;

  p.question {
    color: #000;
    text-align: center;
    font-size: 22px;
    line-height: 28px;
  }
`;
const FormContainer = styled.div`
  margin: 32px auto 0;
  width: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const Form = styled.form`
  margin-top: 32px;
  width: 100%;
  input {
    border-radius: 6px;
    border: 2px solid #005840;
    background-color: transparent;
    padding: 20px 24px;
    width: 100%;
    display: inline-block;
    margin-bottom: 16px;
  }
`;

const ScoreContainer = styled.section`
  margin-top: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Scores = styled.div`
  width: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;

  div.score {
    border-radius: 6px;
    background: #ece7db;
    padding: 10px 16px;
    font-family: Inter;
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    width: 100%;
  }
  .correct {
    color: #005840;
  }
  .incorrect {
    color: #81003b;
  }
`;

const FinalResult = styled.div`
  width: 250px;
  border-radius: 6px;
  background: #cdf545;
  padding: 16px;
  color: #005840;
  font-family: Inter;
  margin: 8px auto 0;
  text-transform: uppercase;

  div.divider {
    height: 1px;
    background: rgba(0, 88, 64, 0.1);
    margin-top: 4px;
    margin-bottom: 16px;
  }

  p.header {
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    text-align: left !important;
  }

  p.score {
    font-size: 14px;
    font-weight: 500;
    line-height: 30px;
    margin-bottom: 16px;
  }
  p.final-score {
    font-weight: 600;
  }
`;

const Message = styled.div`
  color: #020202;
  text-align: center;
  font-family: Inter;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  margin-bottom: 16px;
`;

const levels = [
  { operation: "+", min: 1, max: 20 }, //1
  { operation: "-", min: 1, max: 20 }, //2
  { operation: "+", min: 20, max: 50 }, //3
  { operation: "-", min: 20, max: 50 }, //4
  { operation: "*", min: 1, max: 20 }, //5
  { operation: "/", min: 1, max: 20 }, //6
  { operation: "+", min: 50, max: 150 }, //7
  { operation: "-", min: 50, max: 150 }, //8
  { operation: "*", min: 20, max: 50 }, //9
  { operation: "/", min: 20, max: 50 }, //10
  { operation: "*", min: 50, max: 100 }, //11
  { operation: "/", min: 50, max: 100 }, //12
  { operation: "+", min: 150, max: 500 }, //13
  { operation: "-", min: 150, max: 500 }, //14
  { operation: "special", question: "Само да те провериме, колку е 2+2?" }, //15
];

function generateDivisiblePair(
  minDivisor,
  maxDivisor,
  minMultiplier = 1,
  maxMultiplier = 10
) {
  // Step 1: Generate a divisor within the specified range
  let divisor =
    Math.floor(Math.random() * (maxDivisor - minDivisor + 1)) + minDivisor;
  if (divisor > 20) {
    divisor = divisor + (5 - (divisor % 5));
  }

  // Step 2: Generate a multiplier within the specified range to get the multiple
  const multiplier =
    Math.floor(Math.random() * (maxMultiplier - minMultiplier + 1)) +
    minMultiplier;

  // Step 3: Calculate the multiple, ensuring it's divisible by the divisor
  const dividend = divisor * multiplier;

  return [dividend, divisor];
}

const SpeedArithmeticGame = () => {
  const { t } = useTranslation();
  let navigate = useNavigate();
  // Local Storage
  const guid = localStorage.getItem("guid");
  const speedArithmeticsStatus = localStorage.getItem("speedArithmeticsStatus");
  // States
  const { userInfo, setUserInfo, popupConfig, setPopupConfig } =
    useContext(GlobalContext);
  const [showRules, setShowRules] = useState(false);
  const [duration, setDuration] = useState(null);
  const [currentProblem, setCurrentProblem] = useState("");
  const [currentLevel, setCurrentLevel] = useState(0);
  const [isReversing, setIsReversing] = useState(false);
  const [answer, setAnswer] = useState("");
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [inCorrectAnswers, setInCorrectAnswers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [endTimestamp, setEndTimestamp] = useState(null);

  useEffect(() => {
    const handleContextMenu = (event) => {
      event.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  useEffect(() => {
    //if user has no active assessment session (not registered), redirect user to homepage
    if (guid === null) {
      navigate(API.createRoute("home"));
      return;
    }
    setIsLoading(true);
    API.axios
      .get(API.createApiRoute("user"))
      .then((response) => {
        if (response.data.success) {
          setUserInfo({
            ...userInfo,
            speedArithmeticDuration:
              response.data.data.speed_arithmetic_duration,
            speedArithmeticResult: response.data.data.speed_arithmetic_result,
            memoryMatrixResult: response.data.data.memory_matrix_result,
          });
        }
      })
      .catch((error) => {
        setPopupConfig({
          ...popupConfig,
          show: true,
          subTitle: "You must register first before accessing this page.",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });

    if (speedArithmeticsStatus === "playing") {
      fetchGameDuration();
    }
  }, []);

  useEffect(() => {
    if (!guid) return;

    if (
      userInfo.memoryMatrixResult === null &&
      userInfo.speedArithmeticResult === null
    ) {
      navigate(API.createRoute("memoryGame"));
      return;
    }

    if (
      userInfo.memoryMatrixResult !== null &&
      userInfo.speedArithmeticResult === null
    ) {
      navigate(API.createRoute("result"));
      return;
    }
  }, [userInfo]);

  useEffect(() => {
    displayProblem();
  }, [currentLevel]);

  useEffect(() => {
    let timer;

    if (duration > 0) {
      timer = setInterval(() => {
        setDuration((prevDuration) => {
          if (prevDuration <= 1) {
            clearInterval(timer); // Stop the timer at 0
            endGame(); // Trigger end game when time is up
            return 0;
          }
          return prevDuration - 1; // Decrease by 1 every second
        });
      }, 1000);
    }

    // Cleanup the interval on component unmount or if duration changes
    return () => clearInterval(timer);
  }, [duration]);

  function startGame() {
    fetchGameDuration();
    displayProblem();
    localStorage.setItem("speedArithmeticsStatus", "playing");
  }

  function fetchGameDuration() {
    API.axios
      .post(API.createApiRoute("startSpeedArithmetic"), guid)
      .then((response) => {
        if (response.data.success) {
          if (response.data.data.finished) {
            return;
          }
          if (response.data.data.duration) {
            setDuration(response.data.data.duration);
            setEndTimestamp(response.data.data.duration + 5 + Math.floor(Date.now() / 1000));
          }
        } else {
          setPopupConfig({
            ...popupConfig,
            show: true,
            subTitle: "An error occurred while starting the game.",
          });
        }
      })
      .catch((error) => {
        setPopupConfig({
          ...popupConfig,
          show: true,
          subTitle: "An error occurred while starting the game.",
        });
      });
  }

  function generateProblem(level) {
    if (level.operation === "special") {
      return {
        question: level.question,
        answer: 4,
      };
    }

    let num1, num2, answer;
    do {
      num1 =
        Math.floor(Math.random() * (level.max - level.min + 1)) + level.min;
      num2 =
        Math.floor(Math.random() * (level.max - level.min + 1)) + level.min;

      if (level.operation === "/") {
        //num1 = num2 * Math.floor(Math.random() * (level.max / num2));
        [num1, num2] = generateDivisiblePair(level.min, level.max);
      }

      if (level.operation === "*" && num2 > 20) {
        // alert('a');
        num2 = num2 + (10 - (num2 % 10));
      }

      switch (level.operation) {
        case "+":
          answer = num1 + num2;
          break;
        case "-":
          answer = num1 - num2;
          break;
        case "*":
          answer = num1 * num2;
          break;
        case "/":
          answer = num1 / num2;
          break;
      }
    } while (
      (level.operation === "-" && num1 < num2) ||
      (level.operation === "/" && (num1 === num2 || num1 % num2 !== 0))
    );

    const scenarios = [
      {
        operation: "+",
        questions: [
          "Давид има {X} книги дома и позајмува уште {Y} од библиотеката. Колку книги има вкупно?",
          "Роботот Рико додал {X} линии код во компјутерска програма составена од {Y} линии код. Колку вкупно линии код има сега програмата?",
          "Во 2023 во шумата биле посадени {X} дрвја. Во 2024 група извидници посадила уште {Y}. Колку вкупно дрвја има во шумата?",
          "Тројца астронаути со себе понеле {X} кутии храна, по што од земјата со специјална капсула им биле пратени уште {Y}. Колку вкупно кутии храна имаат астронаутите?",
          "Тимот археолози собрал {X} древни артефакти од храмот, а нивниот водич пронашол уште {Y} во една скриена соба. Колку артефакти имаат заедно?",
          "Во виртуелната училница има {X} аватари, а новите ученици креираа уште {Y}. Колку аватари се вкупно присутни во училницата?",
          "Филип реши {X} задачи од математиката на првиот ден и уште {Y} задачи на вториот ден. Колку задачи реши вкупно?",
          "Во центарот за вселенско истражување, досега откриле {X} нови ѕвезди, а новиот телескоп откри уште {Y}. Колку ѕвезди откриле вкупно?",
          "Тимот на инженери поправил {X} машини минатата недела и уште {Y} оваа недела. Колку машини се поправени вкупно?",
          "На натпреварот по математика се пријавиле {X} учесници првиот ден, а уште {Y} се приклучиле вториот ден. Колку учесници има вкупно на натпреварот?"
        ],
      },
      {
        operation: "-",
        questions: [
          "Од мистериозната кутија со {X} клучеви магионичарот извадил {Y}. Колку клучеви останале во кутијата?",
          "{X} луѓе чекаат да влезат во времеплов. Бројот на слободно места во времепловот е {Y}. Колку луѓе нема да може да влезат?",
          "Астронаутите имаат {X} литри вода на вселенската станица, но потрошија {Y} литри за мисијата. Колку литри вода им остануваат?",
          "Компјутерскиот тим започнал со {X} процесори, но по проверка биле отстранети {Y} неисправни процесори. Колку процесори им останале за работа?",
          "Во текот на натпреварот, тимот постигнал {X} поени, но им биле одземени {Y} поени поради прекршок. Колку поени имаат сега?",
          "Волонтерите засадиле {X} дрвја на почетокот на сезоната, но поради суша, {Y} дрвја не преживеале. Колку дрвја им останале?",
          "Детективот имал {X} осомничени за случајот, но по анализирање на доказите, исклучил {Y} од нив. Колку осомничени му остануваат?",
          "Компанијата имала буџет од {X} долари, но потрошиле {Y} долари на маркетинг кампањата. Колку средства (долари) им остануваат?",
          "Воинот собрал {X} златни монети на своето патување, но по престојот во кралството, потрошил {Y} монети. Колку монети му остануваат?",
          "Доставувачот имал {X} пакети во камионот, од кои {Y} испорачал во првиот град. Колку пакети остануваат за следниот град?"
        ],
      },
      {
        operation: "*",
        questions: [
          "Во една училницата има {X} ученици, а секој ученик има {Y} тетратки. Колку тетракти имаат сите ученици заедно?",
          "Фабрика произведува {X} автомобили за еден час. Колку автомобили ќе произведе фабриката за {Y} часа?",
          "Во лабораторијата, секој научник има {X} микроскопи, а во тимот работат {Y} научници. Колку микроскопи имаат вкупно?",
          "На Brainster Next факултетот, секој студент треба да изработи {X} проекти, а во курсот се запишани {Y} студенти. Колку проекти ќе бидат изработени вкупно?",
          "Астронаутите во вселенската мисија треба да направат {X} експерименти секој ден, а мисијата ќе трае {Y} денови. Колку експерименти ќе извршат за целата мисија?",
          "Во дигиталната библиотека има {X} сервери и секој може да складира {Y} е-книги. Колку е-книги може да складираат сите сервери заедно?",
          "Во фабриката за роботи, секој инженер може да програмира {X} роботи за една недела, а во тимот има {Y} инженери. Колку роботи ќе бидат програмирани за една недела?",
          "Во кампусот, секој студент поседува {X} електронски уреди, а кампусот има {Y} студенти. Колку електронски уреди има вкупно?",
          "На едукативна платформа секој курс содржи {X} лекции, а платформата вкупно има {Y} курсеви. Колку лекции имаат сите курсеви заедно?",
          "Во технолошкиот центар, секој рачен скенер може да скенира {X} артикли на час, а вкупно има {Y} скенери. Колку артикли ќе бидат скенирани за еден час?"
        ],
      },
      {
        operation: "/",
        questions: [
          "Ако имате преостанато {X} гигабајти интернет кои треба да ги поделите подеднакво на {Y} луѓе, колку гигабајти интернет ќе добие секој човек?",
          "Вкупно {X} роботи треба да се распределат низ {Y} градови. Колку роботи ќе има во секој град?",
          "Вашата компанија произведе {X} соларни панели и планира да ги распредели на {Y} згради. Колку соларни панели ќе добие секоја зграда?",
          "Студентите имаат {X} часови за учење и треба да ги поделат на {Y} предмети. Колку часови ќе посветат на секој предмет?",
          "Градот има {X} автобуси кои треба да се распоредат на {Y} рути. Колку автобуси ќе има на секоја рута?",
          "За научен експеримент се потребни {X} милилитри течност, кои треба да се поделат на {Y} епрувети. Колку милилитри ќе има во секоја епрувета?",
          "Тим астронаути собрале {X} ретки минерали од нова планета и планираат да ги испратат на {Y} универзитети за истражување. Колку минерали ќе добие секој универзитет?",
          "За училишен натпревар, учениците собрале {X} постери за настанот и треба да ги распоредат рамномерно во {Y} училници. Колку постери ќе има во секоја училница?",
          "Научниците развиле {X} експериментални уреди и ги носат на {Y} тест локации. Колку уреди ќе се тестираат на секоја локација?",
          "Во училницата има {X} таблети, кои професорот ги распределува рамномерно на {Y} групи за тимска работа. Колку таблети ќе има секоја група?"
        ],
      },
    ];

    // Find the scenario matching the chosen operation
    const chosenScenario = scenarios.find(
      (scenario) => scenario.operation === level.operation
    );

    let question =
      chosenScenario.questions[
      Math.floor(Math.random() * chosenScenario.questions.length)
      ];
    question = question.replace("{X}", num1);
    question = question.replace("{Y}", num2);

    return { question, answer };
  }

  function displayProblem() {
    let newCurrentProblem;

    const levelIndex = isReversing
      ? levels.length - 1 - (currentLevel % levels.length)
      : currentLevel % levels.length;
    newCurrentProblem = generateProblem(levels[levelIndex]);

    setCurrentProblem(newCurrentProblem);
    // setCurrentLevel(levelIndex + 1);
  }

  function checkAnswer(e) {
    if (e) e.preventDefault();
    if (answer.trim() === "") return;
    const userAnswer = parseInt(answer);
    let newCorrectAnswers = correctAnswers;
    let newInCorrectAnswers = inCorrectAnswers;
    let newCurrentLevel = currentLevel;

    if (userAnswer === currentProblem.answer) {
      newCorrectAnswers++;
      newCurrentLevel++;

      if (newCorrectAnswers === levels.length && !isReversing) {
        newCurrentLevel++;
        setIsReversing(true);
      }
    } else {
      newInCorrectAnswers++;
    }

    setCorrectAnswers(newCorrectAnswers);
    setCurrentLevel(newCurrentLevel);
    setInCorrectAnswers(newInCorrectAnswers);
    setAnswer("");

    //after each answer check if the time has passed and end the game if so. 
    //This is due to browsers terminating the tab when the laptop is on battery
    //If that happens when user answers the question after coming back the game will end
    //add 5 seconds just in case
    if (endTimestamp < (Math.floor(Date.now() / 1000))) {
      endGame();
      console.log('end game', endTimestamp, (Math.floor(Date.now() / 1000)));
    }
  }

  function handleShowGameRules() {
    setShowRules(true);
  }
  function handleHideGameRules() {
    setShowRules(false);
  }
  function sendResultData() {
    const data = {
      guid: guid,
      result: correctAnswers,
    };
    API.axios
      .post(API.createApiRoute("resultSpeedArithmetic"), data)
      .then((response) => {
        if (response.data.success) {
          setUserInfo({
            ...userInfo,
            speedArithmeticResult: correctAnswers,
          });
        } else {
          // setErrors({ ...errors, no_term: [response.data.message] });
        }
      })
      .catch((error) => {
        setPopupConfig({
          ...popupConfig,
          show: true,
          subTitle: "An error occurred while ending the game.",
        });
      });
  }
  function endGame() {
    sendResultData();
    setDuration(0);
    localStorage.setItem("speedArithmeticsStatus", "end");
  }
  function handleNextGame() {
    localStorage.setItem("assessment", "not-started");
    navigate(API.createRoute("assessment"));
  }

  return (
    <Container>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="196"
        height="258"
        viewBox="0 0 196 258"
        fill="none"
        className="triangle-left"
      >
        <path
          d="M50.2849 -14.7254L6.20731 -5.3114C4.52742 -4.94628 2.99543 -4.09472 1.81671 -2.87084C0.63798 -1.64697 -0.131161 -0.109287 -0.387596 1.53609L-39.7821 247.788C-39.9928 249.092 -39.8755 250.421 -39.44 251.662C-39.0045 252.903 -38.2639 254.019 -37.2811 254.915C-36.2983 255.812 -35.1025 256.462 -33.7956 256.811C-32.4886 257.159 -31.1092 257.196 -29.775 256.917L188.995 210.193C190.32 209.896 191.556 209.297 192.601 208.446C193.645 207.595 194.467 206.517 194.997 205.302C195.527 204.088 195.75 202.772 195.647 201.466C195.544 200.16 195.118 198.902 194.405 197.797L59.1841 -11.1612C58.2685 -12.5694 56.9248 -13.6659 55.3337 -14.3031C53.7426 -14.9404 51.9806 -15.0877 50.2849 -14.7254ZM2.33331 216.234L7.20085 185.203L12.6468 150.214L33.5653 17.0246L106.299 130.213L125.512 159.934L142.452 186.308L2.33331 216.234Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="350"
        height="348"
        viewBox="0 0 350 348"
        fill="none"
        className="divide-svg"
      >
        <path
          d="M181.789 203.945L265.498 264.319L289.558 230.493L205.849 170.118L168.713 143.126L85.0038 82.7516L60.2507 116.62L143.959 176.994L181.789 203.945Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
        <path
          d="M209.16 121.191C220.941 131.628 238.953 130.539 249.39 118.758C259.827 106.977 258.737 88.9654 246.956 78.5283C235.175 68.0912 217.164 69.1806 206.727 80.9616C196.29 92.7426 197.379 110.754 209.16 121.191Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
        <path
          d="M103.18 268.219C114.961 278.656 132.972 277.567 143.409 265.786C153.846 254.005 152.757 235.993 140.976 225.556C129.195 215.119 111.184 216.209 100.746 227.99C90.3094 239.771 91.3988 257.782 103.18 268.219Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="205"
        height="283"
        viewBox="0 0 205 283"
        fill="none"
        className="plus-svg-left"
      >
        <path
          d="M6.86005 14.7681L28.5066 123.633C28.6591 124.4 28.6645 125.189 28.5223 125.954C28.3801 126.719 28.0931 127.446 27.6778 128.093C27.2626 128.739 26.7271 129.293 26.1019 129.723C25.4767 130.152 24.7741 130.449 24.0343 130.596L-83.3969 151.957C-84.8911 152.254 -86.2018 153.155 -87.0405 154.461C-87.8792 155.766 -88.1773 157.37 -87.8692 158.92L-79.5137 200.942C-79.2049 202.495 -78.3162 203.867 -77.0416 204.758C-75.767 205.65 -74.2101 205.989 -72.7107 205.7L34.7204 184.339C36.1966 184.045 37.7334 184.364 39.0002 185.227C40.267 186.089 41.1626 187.427 41.4945 188.952L63.141 297.817C63.4491 299.367 64.3382 300.735 65.6126 301.62C66.8871 302.506 68.4425 302.837 69.9367 302.54L105.56 295.456C107.055 295.159 108.365 294.259 109.204 292.953C110.043 291.647 110.341 290.043 110.033 288.494L88.3863 179.628C88.0782 178.079 88.3763 176.475 89.215 175.169C90.0537 173.863 91.3644 172.963 92.8586 172.666L200.29 151.304C201.785 150.997 203.094 150.088 203.931 148.777C204.767 147.466 205.064 145.858 204.755 144.305L196.399 102.283C196.091 100.734 195.202 99.3659 193.928 98.4803C192.653 97.5947 191.098 97.2641 189.604 97.5612L82.1724 118.923C80.6963 119.216 79.1595 118.898 77.8927 118.035C76.6259 117.172 75.7302 115.834 75.3984 114.309L53.7518 5.44423C53.4437 3.89473 52.5547 2.52674 51.2802 1.64119C50.0058 0.755599 48.4504 0.424998 46.9561 0.722108L11.3324 7.80546C9.83814 8.10257 8.52751 9.00306 7.68879 10.3088C6.85006 11.6146 6.55195 13.2186 6.86005 14.7681Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="199"
        height="231"
        viewBox="0 0 199 231"
        fill="none"
        className="plus-svg-right"
      >
        <path
          d="M260.986 115.054L166.584 89.759C165.919 89.5807 165.294 89.2778 164.745 88.8676C164.196 88.4574 163.734 87.9479 163.385 87.3682C163.036 86.7885 162.807 86.1499 162.712 85.4889C162.616 84.8279 162.655 84.1576 162.827 83.516L187.789 -9.64259C188.136 -10.9383 187.935 -12.324 187.231 -13.4948C186.526 -14.6656 185.375 -15.5256 184.032 -15.8856L147.592 -25.6494C146.246 -26.0103 144.816 -25.8427 143.616 -25.1833C142.416 -24.5238 141.542 -23.4261 141.186 -22.1299L116.224 71.0287C115.881 72.3089 115.031 73.3984 113.856 74.0629C112.682 74.7274 111.277 74.9138 109.943 74.582L15.541 49.2871C14.1973 48.927 12.7708 49.0965 11.5752 49.7581C10.3796 50.4198 9.5129 51.5194 9.16572 52.8151L0.888492 83.7061C0.541305 85.0019 0.7421 86.3875 1.44671 87.5583C2.15131 88.7291 3.30197 89.5891 4.64562 89.9491L99.0476 115.244C100.391 115.604 101.542 116.464 102.247 117.635C102.951 118.806 103.152 120.191 102.805 121.487L77.843 214.646C77.5034 215.946 77.7112 217.334 78.4211 218.505C79.1309 219.676 80.2851 220.536 81.6317 220.897L118.071 230.661C119.414 231.021 120.841 230.852 122.037 230.19C123.232 229.528 124.099 228.429 124.446 227.133L149.408 133.974C149.751 132.694 150.601 131.605 151.775 130.94C152.95 130.276 154.355 130.089 155.689 130.421L250.091 155.716C251.434 156.076 252.861 155.907 254.057 155.245C255.252 154.583 256.119 153.484 256.466 152.188L264.743 121.297C265.09 120.001 264.89 118.616 264.185 117.445C263.48 116.274 262.33 115.414 260.986 115.054Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="272"
        height="317"
        viewBox="0 0 272 317"
        fill="none"
        className="triangle-right"
      >
        <path
          d="M98.724 0.499593L47.9103 12.3397C45.9738 12.7983 44.2189 13.8167 42.8809 15.2585C41.5428 16.7002 40.6856 18.4964 40.424 20.4063L0.104569 306.263C-0.111391 307.776 0.0529391 309.312 0.583556 310.739C1.11417 312.166 1.99529 313.442 3.15199 314.458C4.30869 315.475 5.70661 316.202 7.22673 316.577C8.74683 316.953 10.3441 316.965 11.8825 316.614L264.086 257.849C265.613 257.477 267.031 256.757 268.222 255.75C269.413 254.742 270.341 253.477 270.928 252.06C271.516 250.643 271.745 249.115 271.598 247.606C271.451 246.096 270.931 244.649 270.082 243.385L109.1 4.43414C108.011 2.82395 106.432 1.5836 104.577 0.880142C102.721 0.176677 100.679 0.0438691 98.724 0.499593ZM48.173 268.84L53.1419 232.82L58.6954 192.208L80.053 37.6052L166.66 167.051L189.534 201.039L209.705 231.202L48.173 268.84Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
      </svg>
      <svg
        width="250"
        height="223"
        viewBox="0 0 250 223"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="divider-right"
      >
        <path
          d="M187.825 164.957L295.851 135.883L283.872 92.5146L175.846 121.588L127.747 134.344L19.7217 163.417L31.2617 207.398L139.287 178.325L187.825 164.957Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
        <path
          d="M136.967 85.335C153.801 82.5705 165.207 66.6825 162.443 49.8481C159.678 33.0138 143.79 21.6078 126.956 24.3723C110.122 27.1368 98.7157 43.0248 101.48 59.8592C104.245 76.6935 120.133 88.0995 136.967 85.335Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
        <path
          d="M188.084 275.015C204.919 272.251 216.324 256.363 213.56 239.528C210.795 222.694 194.908 211.288 178.073 214.052C161.239 216.817 149.833 232.705 152.597 249.539C155.362 266.374 171.25 277.78 188.084 275.015Z"
          fill="#CDF545"
          fill-opacity="0.05"
        />
      </svg>
      <Alert />
      <InnerContainer>
        <UltimathLogo />
        <Stepper currentChallenge="speed-arithmetic" activeStep={1} />
        <GameTitle>
          <h1>Ментална математика</h1>
          {speedArithmeticsStatus !== "playing" &&
            userInfo.speedArithmeticDuration === null ? (
            <p>Кликни старт за да го започнеш предизвикот.</p>
          ) : (
            ""
          )}
        </GameTitle>

        <div>
          {speedArithmeticsStatus !== "playing" &&
            userInfo.speedArithmeticResult === null && (
              <GameDuration className="text-center">
                <p>{userInfo.speedArithmeticDuration}</p>
                <span>секунди</span>
              </GameDuration>
            )}

          {!showRules &&
            speedArithmeticsStatus !== "playing" &&
            userInfo.speedArithmeticResult === null && (
              <div className="button-container">
                <CustomButton
                  bgColor="#005840"
                  color="#CDF545"
                  onClick={startGame}
                >
                  СТАРТ
                </CustomButton>

                <CustomButton
                  bgColor="rgba(0, 88, 64, 0.20)"
                  color="#005840"
                  onClick={handleShowGameRules}
                >
                  КАКО ФУНКЦИОНИРА
                </CustomButton>
              </div>
            )}

          {showRules && userInfo.speedArithmeticResult === null && (
            <GameRules onHide={handleHideGameRules}>
              <video width="100%" controls>
                <source src={speedArithmeticsVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </GameRules>
          )}

          {speedArithmeticsStatus === "playing" &&
            userInfo.speedArithmeticResult === null && (
              <>
                <GameDuration className="text-center">
                  <p>{duration}</p>
                  <span>секунди</span>
                </GameDuration>
                <div>
                  <Level>Ниво: {currentLevel + 1}</Level>
                  <CurrentProblemContainer>
                    <p className="question">{currentProblem.question}</p>
                  </CurrentProblemContainer>
                  <FormContainer>
                    <Form
                      onSubmit={(event) => {
                        checkAnswer(event); // Pass event when submitting the form
                      }}
                      onKeyPress={(event) => {
                        if (event.key === "Enter") {
                          checkAnswer(event); // Pass event when pressing Enter
                        }
                      }}
                    >
                      <input
                        type="number"
                        placeholder="Одговор"
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                      />
                    </Form>
                    <CustomButton
                      bgColor="#005840"
                      color="#CDF545"
                      type="button"
                      onClick={() => checkAnswer(null)}
                    >
                      продолжи
                    </CustomButton>
                  </FormContainer>

                  <ScoreContainer>
                    <Scores>
                      <div className="score correct">
                        Точни одговори: {correctAnswers}
                      </div>
                      <div className="score incorrect">
                        Погрешни одговори: {inCorrectAnswers}
                      </div>
                    </Scores>
                  </ScoreContainer>
                </div>
              </>
            )}

          {userInfo.speedArithmeticResult !== null && (
            <>
              <GameDuration className="text-center">
                <p>0</p>
                <span>секунди</span>
              </GameDuration>
              <Message>Времето истече!</Message>

              <FinalResult>
                Финален резултат: {userInfo.speedArithmeticResult}
              </FinalResult>
              <div className="button-container">
                <CustomButton
                  bgColor="#005840"
                  color="#CDF545"
                  onClick={handleNextGame}
                >
                  следно ниво
                </CustomButton>
              </div>
            </>
          )}
        </div>
      </InnerContainer>
    </Container>
  );
};

export default withTheme(SpeedArithmeticGame);
